// web/src/components/Common/AlertBanner.tsx
// Reusable alert banner component for displaying critical information
// Supports: subscription limits, system warnings, info messages, etc.

import React from 'react';
import { useTranslation } from 'react-i18next';
import './AlertBanner.css';

export type AlertType = 'warning' | 'error' | 'info' | 'success' | 'subscription';

export interface AlertBannerProps {
  type: AlertType;
  title: string;
  message: string;
  icon?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  actionText?: string;
  onAction?: () => void;
  actionLink?: string;
  metadata?: Record<string, any>;
  className?: string;
}

const AlertBanner: React.FC<AlertBannerProps> = ({
  type,
  title,
  message,
  icon,
  dismissible = true,
  onDismiss,
  actionText,
  onAction,
  actionLink,
  metadata,
  className = '',
}) => {
  const { t } = useTranslation();

  const getDefaultIcon = (): string => {
    switch (type) {
      case 'warning':
        return 'fas fa-exclamation-triangle';
      case 'error':
        return 'fas fa-times-circle';
      case 'info':
        return 'fas fa-info-circle';
      case 'success':
        return 'fas fa-check-circle';
      case 'subscription':
        return 'fas fa-credit-card';
      default:
        return 'fas fa-bell';
    }
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else if (actionLink) {
      window.location.href = actionLink;
    }
  };

  return (
    <div className={`alert-banner alert-banner-${type} ${className}`}>
      <div className="alert-banner-icon">
        <i className={icon || getDefaultIcon()}></i>
      </div>

      <div className="alert-banner-content">
        <div className="alert-banner-title">{title}</div>
        <div className="alert-banner-message">{message}</div>

        {metadata && (
          <div className="alert-banner-metadata">
            {Object.entries(metadata).map(([key, value]) => (
              <span key={key} className="metadata-item">
                <strong>{key}:</strong> {String(value)}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="alert-banner-actions">
        {actionText && (onAction || actionLink) && (
          <button
            className="alert-banner-action-btn"
            onClick={handleAction}
          >
            {actionText}
          </button>
        )}

        {dismissible && (
          <button
            className="alert-banner-dismiss-btn"
            onClick={onDismiss}
            title={t('common.dismiss')}
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertBanner;

// Subscription Limit Banner - specialized component for subscription warnings
export interface SubscriptionLimitBannerProps {
  currentUsage: number;
  maxAllowed: number;
  resourceType: 'targets' | 'scans' | 'projects' | 'users';
  warningThreshold?: number; // Percentage at which to show warning (default 80%)
  onUpgrade?: () => void;
  onDismiss?: () => void;
  dismissible?: boolean;
}

export const SubscriptionLimitBanner: React.FC<SubscriptionLimitBannerProps> = ({
  currentUsage,
  maxAllowed,
  resourceType,
  warningThreshold = 80,
  onUpgrade,
  onDismiss,
  dismissible = true,
}) => {
  const { t } = useTranslation();

  const usagePercent = maxAllowed > 0 ? (currentUsage / maxAllowed) * 100 : 0;
  const isOverLimit = currentUsage >= maxAllowed;
  const isNearLimit = usagePercent >= warningThreshold && !isOverLimit;

  if (!isOverLimit && !isNearLimit) {
    return null; // Don't show banner if usage is low
  }

  const resourceLabel = t(`subscription.resources.${resourceType}`, resourceType);

  const type: AlertType = isOverLimit ? 'error' : 'warning';
  const title = isOverLimit
    ? t('subscription.limitExceeded', 'Limit Exceeded')
    : t('subscription.nearLimit', 'Approaching Limit');

  const message = isOverLimit
    ? t('subscription.limitExceededMessage', {
        current: currentUsage,
        max: maxAllowed,
        resource: resourceLabel,
        defaultValue: `You have exceeded your ${resourceLabel} limit (${currentUsage}/${maxAllowed}). Some features may be restricted.`
      })
    : t('subscription.nearLimitMessage', {
        current: currentUsage,
        max: maxAllowed,
        resource: resourceLabel,
        percent: Math.round(usagePercent),
        defaultValue: `You are using ${Math.round(usagePercent)}% of your ${resourceLabel} limit (${currentUsage}/${maxAllowed}).`
      });

  return (
    <AlertBanner
      type={type}
      title={title}
      message={message}
      icon="fas fa-chart-pie"
      dismissible={dismissible}
      onDismiss={onDismiss}
      actionText={t('subscription.upgrade', 'Upgrade Plan')}
      onAction={onUpgrade}
      metadata={{
        [t('subscription.usage', 'Usage')]: `${currentUsage} / ${maxAllowed}`,
        [t('subscription.percent', 'Percent')]: `${Math.round(usagePercent)}%`
      }}
    />
  );
};

// Trial Banner - for trial period warnings
export interface TrialBannerProps {
  daysRemaining: number;
  onUpgrade?: () => void;
  onDismiss?: () => void;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({
  daysRemaining,
  onUpgrade,
  onDismiss,
}) => {
  const { t } = useTranslation();

  const isExpiringSoon = daysRemaining <= 7;
  const type: AlertType = daysRemaining <= 3 ? 'error' : isExpiringSoon ? 'warning' : 'info';

  const title = isExpiringSoon
    ? t('subscription.trialExpiringSoon', 'Trial Expiring Soon')
    : t('subscription.trialActive', 'Trial Period');

  const message = t('subscription.trialMessage', {
    days: daysRemaining,
    defaultValue: `Your trial period ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Upgrade now to keep full access.`
  });

  return (
    <AlertBanner
      type={type}
      title={title}
      message={message}
      icon="fas fa-clock"
      dismissible={daysRemaining > 3}
      onDismiss={onDismiss}
      actionText={t('subscription.upgradeNow', 'Upgrade Now')}
      onAction={onUpgrade}
    />
  );
};

// Partial Scan Warning Banner - for discovery-only scan results
export interface PartialScanBannerProps {
  discoveryOnlyCount: number;
  maxTargets: number;
  scanName?: string;
  onViewDetails?: () => void;
  onDismiss?: () => void;
}

export const PartialScanBanner: React.FC<PartialScanBannerProps> = ({
  discoveryOnlyCount,
  maxTargets,
  scanName,
  onViewDetails,
  onDismiss,
}) => {
  const { t } = useTranslation();

  if (discoveryOnlyCount <= 0) {
    return null;
  }

  const title = t('subscription.partialScan', 'Partial Scan Results');
  const message = t('subscription.partialScanMessage', {
    count: discoveryOnlyCount,
    max: maxTargets,
    scan: scanName || '',
    defaultValue: `${discoveryOnlyCount} target${discoveryOnlyCount !== 1 ? 's were' : ' was'} only scanned for discovery (ports/services) due to your subscription limit of ${maxTargets} targets. Full vulnerability scanning was not performed on these targets.`
  });

  return (
    <AlertBanner
      type="warning"
      title={title}
      message={message}
      icon="fas fa-search"
      dismissible={true}
      onDismiss={onDismiss}
      actionText={t('common.viewDetails', 'View Details')}
      onAction={onViewDetails}
      metadata={{
        [t('subscription.discoveryOnly', 'Discovery Only')]: discoveryOnlyCount,
        [t('subscription.planLimit', 'Plan Limit')]: maxTargets
      }}
    />
  );
};

// System Message Banner - for admin announcements
export interface SystemMessageBannerProps {
  id: string;
  title: string;
  message: string;
  type: AlertType;
  actionUrl?: string;
  actionText?: string;
  onDismiss?: (id: string) => void;
}

export const SystemMessageBanner: React.FC<SystemMessageBannerProps> = ({
  id,
  title,
  message,
  type,
  actionUrl,
  actionText,
  onDismiss,
}) => {
  return (
    <AlertBanner
      type={type}
      title={title}
      message={message}
      dismissible={true}
      onDismiss={() => onDismiss?.(id)}
      actionText={actionText}
      actionLink={actionUrl}
    />
  );
};

// Subscription Expired Banner - critical banner for expired subscriptions
export type SubscriptionExpiredReason =
  | 'subscription_expired'
  | 'trial_expired'
  | 'subscription_suspended'
  | 'subscription_canceled'
  | 'pending_approval';

export interface SubscriptionExpiredBannerProps {
  reason: SubscriptionExpiredReason;
  onUpgrade?: () => void;
  trialEndDate?: string;
  className?: string;
}

export const SubscriptionExpiredBanner: React.FC<SubscriptionExpiredBannerProps> = ({
  reason,
  onUpgrade,
  trialEndDate,
  className = '',
}) => {
  const { t } = useTranslation('common');

  const getTitle = (): string => {
    switch (reason) {
      case 'trial_expired':
        return t('subscriptions.enforcement.trialExpired', 'Trial Expired');
      case 'subscription_expired':
        return t('subscriptions.enforcement.expired', 'Subscription Expired');
      case 'subscription_suspended':
        return t('subscriptions.enforcement.subscriptionSuspended', 'Subscription Suspended');
      case 'subscription_canceled':
        return t('subscriptions.enforcement.subscriptionCanceled', 'Subscription Canceled');
      case 'pending_approval':
        return t('subscriptions.status.pendingApproval', 'Pending Approval');
      default:
        return t('subscriptions.enforcement.expired', 'Subscription Expired');
    }
  };

  const getMessage = (): string => {
    switch (reason) {
      case 'trial_expired':
        return t('subscriptions.enforcement.trialExpiredMessage',
          'Your trial has expired. Please upgrade to continue using all features.');
      case 'subscription_expired':
        return t('subscriptions.enforcement.expiredCannotScan',
          'Your subscription has expired. Please upgrade to create or start scans.');
      case 'subscription_suspended':
        return t('subscriptions.enforcement.contactSupport',
          'Please contact support for assistance.');
      case 'subscription_canceled':
        return t('subscriptions.canceledNotice',
          'Your subscription has been canceled. Please reactivate to continue.');
      case 'pending_approval':
        return t('subscriptions.pendingApprovalMessage',
          'Your account is pending approval. Some features may be restricted.');
      default:
        return t('subscriptions.enforcement.expiredCannotScan',
          'Your subscription has expired. Please upgrade to create or start scans.');
    }
  };

  const getIcon = (): string => {
    switch (reason) {
      case 'trial_expired':
        return 'fas fa-clock';
      case 'subscription_suspended':
        return 'fas fa-pause-circle';
      case 'subscription_canceled':
        return 'fas fa-times-circle';
      case 'pending_approval':
        return 'fas fa-hourglass-half';
      default:
        return 'fas fa-exclamation-triangle';
    }
  };

  const getType = (): AlertType => {
    switch (reason) {
      case 'pending_approval':
        return 'warning';
      default:
        return 'error';
    }
  };

  return (
    <AlertBanner
      type={getType()}
      title={getTitle()}
      message={getMessage()}
      icon={getIcon()}
      dismissible={false}
      actionText={reason !== 'pending_approval' ? t('subscriptions.upgradeNow', 'Upgrade Now') : undefined}
      onAction={reason !== 'pending_approval' ? onUpgrade : undefined}
      className={`subscription-expired-banner ${className}`}
      metadata={trialEndDate ? {
        [t('subscriptions.trialEnds', 'Trial Ended')]: trialEndDate
      } : undefined}
    />
  );
};
